import { useRef } from 'preact/hooks'
import { useSettingIds } from './setting-row.tsx'
import styles from './segmented.module.css'

interface Segment<T extends string> {
  readonly value: T
  readonly label: string
  /** Shown, but not choosable. Used for the Custom preset. */
  readonly disabled?: boolean
}

interface SegmentedProps<T extends string> {
  readonly value: T
  readonly options: readonly Segment<T>[]
  readonly onChange: (value: T) => void
}

function nextEnabled<T extends string>(
  options: readonly Segment<T>[],
  from: number,
  delta: number,
): number {
  for (let step = 1; step <= options.length; step += 1) {
    const index = (from + delta * step + options.length) % options.length
    if (!options[index]?.disabled) return index
  }
  return from
}

/** A radio group for a small enum. Arrow keys skip disabled options. */
export function Segmented<T extends string>({ value, options, onChange }: SegmentedProps<T>) {
  const { id, labelId, describedBy } = useSettingIds()
  const buttons = useRef<(HTMLButtonElement | null)[]>([])
  const selected = options.findIndex((option) => option.value === value)

  const choose = (index: number): void => {
    const option = options[index]
    if (!option || option.disabled) return
    onChange(option.value)
    buttons.current[index]?.focus()
  }

  return (
    <div
      id={id}
      class={styles.group}
      role="radiogroup"
      aria-labelledby={labelId}
      aria-describedby={describedBy}
    >
      {options.map((option, index) => {
        const checked = option.value === value
        return (
          <button
            key={option.value}
            ref={(node) => {
              buttons.current[index] = node
            }}
            type="button"
            role="radio"
            class={styles.option}
            aria-checked={checked}
            aria-disabled={option.disabled ? true : undefined}
            tabIndex={index === (selected === -1 ? 0 : selected) ? 0 : -1}
            onClick={() => {
              choose(index)
            }}
            onKeyDown={(event) => {
              const delta =
                event.key === 'ArrowRight' || event.key === 'ArrowDown'
                  ? 1
                  : event.key === 'ArrowLeft' || event.key === 'ArrowUp'
                    ? -1
                    : 0
              if (delta === 0) return
              event.preventDefault()
              choose(nextEnabled(options, index, delta))
            }}
          >
            {option.label}
          </button>
        )
      })}
    </div>
  )
}
